import { ChevronLeft } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import AuthLayout from '../components/AuthLayout'
import { CustomOTPInput } from '../components/CustomOtp'
import { AuthService } from '../utils/authService'

export default function VerifyOtp() {
  const [otp, setOtp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    // Get email from localStorage (stored during login)
    const storedEmail = localStorage.getItem('loginEmail')
    if (!storedEmail) {
      navigate('/login')
      return
    }
    setEmail(storedEmail)
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit code')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const response = await AuthService.verifyOtp({
        email,
        otp,
      })

      // Store tokens and navigate to dashboard
      AuthService.setTokens(
        response.response.accessToken,
        response.response.refreshToken
      )

      // Clear the stored email
      localStorage.removeItem('loginEmail')

      navigate('/dashboard')
    } catch (err: any) {
      // Extract the exact error message from API response
      let errorMessage = 'Verification failed. Please try again.'

      if (err.response?.message) {
        errorMessage = err.response.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    if (!email) return

    setIsLoading(true)
    setError('')

    try {
      await AuthService.resendOtp({ email })
      // Show success message or handle success
    } catch (err: any) {
      // Extract the exact error message from API response
      let errorMessage = 'Failed to resend code. Please try again.'

      if (err.response?.message) {
        errorMessage = err.response.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <div className='space-y-6'>
        <Link
          to='/login'
          className='inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 flex-row w-full justify-start'
        >
          <ChevronLeft className='w-6 h-6' color='#F04438' />
          <div className='text-[#231F20] font-[600] text-[16px]'>Back</div>
        </Link>

        <div className='space-y-2'>
          <h1 className='text-[36px] font-[700] text-[#181D27] text-start'>
            Two-Step Verification
          </h1>
          <div className='text-[14px] font-[400] text-gray-600 text-start'>
            We sent a verification code to your email. Enter the code from the
            email address in the field below.{' '}
            <b className='text-[#181D27] font-[700] text-[14px]'>
              {email || 'your email'}
            </b>
          </div>
        </div>

        <form onSubmit={handleSubmit} className='space-y-6'>
          {error && (
            <div className='p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md'>
              {error}
            </div>
          )}

          <div className='space-y-2'>
            <div className='text-sm font-medium text-gray-900 text-start'>
              Type your 6 digit security code
            </div>
            <div className='flex justify-start w-full'>
              <CustomOTPInput length={6} value={otp} onChange={setOtp} />
            </div>
          </div>

          <button
            type='submit'
            disabled={isLoading}
            className='w-full h-12 bg-[#001B38] rounded-[8px] cursor-pointer hover:bg-[#0A2540]/90 text-white disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {isLoading ? 'Verifying...' : 'Sign In'}
          </button>
        </form>
        <div className='flex flex-row items-center gap-2 justify-start'>
          <div className='text-start text-sm text-gray-600'>
            Didnt get the code?{' '}
          </div>
          <button
            type='button'
            onClick={handleResend}
            className='text-gray-900 font-semibold hover:underline'
          >
            Resend
          </button>
        </div>
      </div>
    </AuthLayout>
  )
}
